import React, { useState, useEffect } from 'react';
import { 
  Home, Upload, Music, Users, BarChart3, Store, Settings, 
  User, Menu, X, LogOut, Crown
} from 'lucide-react';
import { mockUser } from '../../data/mockData';
import { useMediaQuery } from '../../hooks/useMediaQuery';

interface SidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  isOpen?: boolean;
  onToggle?: () => void;
}

const navigation = [
  { id: 'dashboard', label: 'Dashboard', icon: Home },
  { id: 'distribute', label: 'Distribute Music', icon: Upload },
  { id: 'songs', label: 'Live Songs', icon: Music },
  { id: 'artists', label: 'Artists', icon: Users },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'stores', label: 'Distribution Stores', icon: Store },
  { id: 'settings', label: 'Settings', icon: Settings },
];

export const Sidebar: React.FC<SidebarProps> = ({ 
  activeTab, 
  onTabChange, 
  isOpen = true, 
  onToggle 
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    if (isMobile) {
      setIsCollapsed(true);
    }
  }, [isMobile]);
  
  const handleToggle = () => {
    if (isMobile && onToggle) {
      onToggle();
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  if (isMobile) {
    return (
      <>
        {/* Mobile backdrop */}
        {isOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={onToggle}
          />
        )}
        
        {/* Mobile sidebar */}
        <div className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-black-900 border-r border-gray-800 flex flex-col
          transform transition-transform duration-300 ease-in-out md:hidden
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          {renderSidebarContent()}
        </div>
      </>
    );
  }

  return (
    <div className={`
      h-screen bg-black-900 border-r border-gray-800 flex flex-col
      transition-all duration-300 ease-in-out
      ${isCollapsed ? 'w-16' : 'w-64'}
    `}>
      {renderSidebarContent()}
    </div>
  );

  function renderSidebarContent() {
    return (
      <>
      {/* Header */}
      <div className="p-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 gold-gradient rounded-lg flex items-center justify-center">
                <Crown className="w-5 h-5 text-black" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white font-display">Naad Music</h1>
                <p className="text-xs text-gold-400">Distribution Platform</p>
              </div>
            </div>
          )}
          <button
            onClick={handleToggle}
            className="p-1 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            {isCollapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
          </button>
        </div>
      </div>
      
      {/* User Profile */}
      <div className="p-4 border-b border-gray-800">
        <div className="flex items-center space-x-3">
          <img
            src={mockUser.avatar}
            alt={mockUser.name}
            className="w-10 h-10 rounded-full border-2 border-gold-500"
          />
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{mockUser.name}</p>
              <p className="text-xs text-gold-400 capitalize">{mockUser.plan} Plan</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2">
        {navigation.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          
          return (
            <button
              key={item.id}
              onClick={() => onTabChange(item.id)}
              className={`
                w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg font-medium
                transition-all duration-200 text-left
                ${isActive 
                  ? 'gold-gradient text-black shadow-lg' 
                  : 'text-gray-300 hover:text-white hover:bg-gray-800'
                }
              `}
            >
              <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-black' : ''}`} />
              {!isCollapsed && (
                <span className="truncate">{item.label}</span>
              )}
            </button>
          );
        })}
      </nav>
      
      {/* Footer */}
      <div className="p-4 border-t border-gray-800">
        <button
          className={`
            w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg
            text-gray-300 hover:text-white hover:bg-gray-800
            transition-colors duration-200
          `}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!isCollapsed && <span>Logout</span>}
        </button>
      </div>
      </>
    );
  }
};